import { InputGroup, Presentation, Slide, UNGROUPED_LABEL } from './use-input-groupings';

export const SlideConfigStatus = {
  NOT_CONFIGURED: 'not-configured',
  PARTIALLY_CONFIGURED: 'partially-configured',
  FULLY_CONFIGURED: 'fully-configured'
} as const;
export type SlideConfigStatusType = (typeof SlideConfigStatus)[keyof typeof SlideConfigStatus];

export const countSlideInputs = (slide: Slide): number =>
  slide.inputGroups.reduce((total, group) => total + group.inputs.length, 0);

export const getSlideConfigStatus = (slide: Slide): SlideConfigStatusType => {
  if (countSlideInputs(slide) === 0) {
    return SlideConfigStatus.FULLY_CONFIGURED;
  }

  const ungroupedInputs =
    slide.inputGroups.find(g => g.label === UNGROUPED_LABEL)?.inputs.length || 0;
  const hasGroups = slide.inputGroups.some(g => g.label !== UNGROUPED_LABEL);

  // Has inputs but no groups created
  if (!hasGroups) {
    return SlideConfigStatus.NOT_CONFIGURED;
  }

  // All inputs are grouped (no ungrouped inputs)
  if (ungroupedInputs === 0) {
    return SlideConfigStatus.FULLY_CONFIGURED;
  }

  // Some inputs are grouped, some are not
  return SlideConfigStatus.PARTIALLY_CONFIGURED;
};

// Replace a single slide's contents within a presentation, returning a new presentation.
const withUpdatedSlide = (
  presentation: Presentation,
  slideId: string,
  updater: (slide: Slide) => Slide
): Presentation => ({
  ...presentation,
  slides: presentation.slides.map(slide => (slide.slideId === slideId ? updater(slide) : slide))
});

// Return a new array with the elements at indices i and j swapped.
const swap = <T>(arr: T[], i: number, j: number): T[] => {
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

// Replace a slide's non-ungrouped groups, keeping the ungrouped group first.
const withGroups = (slide: Slide, groups: InputGroup[]): Slide => {
  const ungroupedGroup = slide.inputGroups.find(g => g.label === UNGROUPED_LABEL);
  return { ...slide, inputGroups: ungroupedGroup ? [ungroupedGroup, ...groups] : groups };
};

export const moveGroupUp = (
  presentation: Presentation,
  slideId: string,
  groupId: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const groups = slide.inputGroups.filter(g => g.label !== UNGROUPED_LABEL);
    const index = groups.findIndex(g => g.groupId === groupId);
    if (index <= 0) {
      return slide;
    }
    return withGroups(slide, swap(groups, index, index - 1));
  });

export const moveGroupDown = (
  presentation: Presentation,
  slideId: string,
  groupId: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const groups = slide.inputGroups.filter(g => g.label !== UNGROUPED_LABEL);
    const index = groups.findIndex(g => g.groupId === groupId);
    if (index < 0 || index >= groups.length - 1) {
      return slide;
    }
    return withGroups(slide, swap(groups, index, index + 1));
  });

// Replace the inputs of a single group within a slide.
const withGroupInputs = (slide: Slide, groupId: string, inputs: InputGroup['inputs']): Slide => ({
  ...slide,
  inputGroups: slide.inputGroups.map(group =>
    group.groupId === groupId ? { ...group, inputs } : group
  )
});

export const moveInputUp = (
  presentation: Presentation,
  slideId: string,
  groupId: string,
  inputName: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const group = slide.inputGroups.find(g => g.groupId === groupId);
    if (!group) {
      return slide;
    }
    const index = group.inputs.findIndex(input => input.inputName === inputName);
    if (index <= 0) {
      return slide;
    }
    return withGroupInputs(slide, groupId, swap(group.inputs, index, index - 1));
  });

export const moveInputDown = (
  presentation: Presentation,
  slideId: string,
  groupId: string,
  inputName: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const group = slide.inputGroups.find(g => g.groupId === groupId);
    if (!group) {
      return slide;
    }
    const index = group.inputs.findIndex(input => input.inputName === inputName);
    if (index < 0 || index >= group.inputs.length - 1) {
      return slide;
    }
    return withGroupInputs(slide, groupId, swap(group.inputs, index, index + 1));
  });

export const addGroup = (
  presentation: Presentation,
  slideId: string,
  newGroupId: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const groupCount = slide.inputGroups.filter(g => g.label !== UNGROUPED_LABEL).length;
    const newGroup: InputGroup = {
      groupId: newGroupId,
      label: `Group ${groupCount + 1}`,
      inputs: []
    };
    return { ...slide, inputGroups: [...slide.inputGroups, newGroup] };
  });

export const replaceGroup = (
  presentation: Presentation,
  slideId: string,
  groupId: string,
  updatedGroup: InputGroup
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => ({
    ...slide,
    inputGroups: slide.inputGroups.map(group => (group.groupId === groupId ? updatedGroup : group))
  }));

export const deleteGroup = (
  presentation: Presentation,
  slideId: string,
  groupId: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => ({
    ...slide,
    inputGroups: slide.inputGroups.filter(group => group.groupId !== groupId)
  }));

export const addInputToGroup = (
  presentation: Presentation,
  slideId: string,
  groupId: string,
  inputName: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const ungroupedGroup = slide.inputGroups.find(g => g.label === UNGROUPED_LABEL);
    const inputToMove = ungroupedGroup?.inputs.find(input => input.inputName === inputName);
    if (!inputToMove) {
      return slide;
    }

    return {
      ...slide,
      inputGroups: slide.inputGroups.map(group => {
        if (group.groupId === groupId) {
          // Append to the target group — order is array position (recomputed on save).
          return { ...group, inputs: [...group.inputs, inputToMove] };
        }
        if (group.label === UNGROUPED_LABEL) {
          // Remove from ungrouped
          return {
            ...group,
            inputs: group.inputs.filter(input => input.inputName !== inputName)
          };
        }
        return group;
      })
    };
  });

export const removeInputFromGroup = (
  presentation: Presentation,
  slideId: string,
  groupId: string,
  inputName: string
): Presentation =>
  withUpdatedSlide(presentation, slideId, slide => {
    const sourceGroup = slide.inputGroups.find(g => g.groupId === groupId);
    const inputToMove = sourceGroup?.inputs.find(input => input.inputName === inputName);
    if (!inputToMove) {
      return slide;
    }

    const hasUngrouped = slide.inputGroups.some(g => g.label === UNGROUPED_LABEL);

    const inputGroups = slide.inputGroups.map(group => {
      if (group.groupId === groupId) {
        // Remove from source group
        return {
          ...group,
          inputs: group.inputs.filter(input => input.inputName !== inputName)
        };
      }
      if (group.label === UNGROUPED_LABEL) {
        // Return the input to the ungrouped bucket (order is array position).
        return { ...group, inputs: [...group.inputs, inputToMove] };
      }
      return group;
    });

    // If no ungrouped group exists, create one with the removed input
    if (!hasUngrouped) {
      inputGroups.push({
        groupId: 'ungrouped',
        label: UNGROUPED_LABEL,
        inputs: [inputToMove]
      });
    }

    return { ...slide, inputGroups };
  });
